// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Chip, Tooltip } from '@mui/material';
import type { Document } from '@shared/data-access/types/engine-types';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { AppIcon, Table } from '@shared/ui-kit';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentsRowActions } from '../documents-row-actions/documents-row-actions';
import { useDocumentCatalog } from '../../hooks/use-document-catalog';
import { getDocumentExpiryHint } from '../../utils/get-document-expiry-hint';

export interface UseDocumentsTableProps {
  readonly data: Document[];
  readonly onDownload: (document: Document) => void;
  readonly onViewDetail: (document: Document) => void;
  readonly onDelete: (document: Document) => void;
}

const columnHelper = createColumnHelper<Document>();

const scopeChipColor: Record<DocumentScope, 'default' | 'warning' | 'info'> = {
  [DocumentScope.Personal]: 'default',
  [DocumentScope.Company]: 'warning',
  [DocumentScope.SilverAlpha]: 'info',
};

const expiryDotClassName = {
  warning: 'bg-warning',
  negative: 'bg-negative',
} as const;

export const useDocumentsTable = ({
  data,
  onDownload,
  onViewDetail,
  onDelete,
}: UseDocumentsTableProps) => {
  const { t, i18n } = useTranslation();
  const { categoryLabels, subcategoryLabels } = useDocumentCatalog();

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: 'document',
        header: t('documents.table.columns.document', { ns: 'components' }),
        enableHiding: false,
        minSize: 260,
        size: 360,
        maxSize: 480,
        cell: info => {
          const document = info.row.original;
          const fileCount = document.files?.length ?? 0;
          const scopeLabel = t(`documents.scope.${document.scope}`, {
            ns: 'components',
          });

          return (
            <div className="flex min-w-0 items-start gap-x-3 py-1">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-section">
                <AppIcon icon="FileText" color="inherit" size="lg" />
              </div>

              <div className="min-w-0">
                <Tooltip title={document.title} placement="top">
                  <button
                    type="button"
                    onClick={() => onViewDetail(document)}
                    className="max-w-full cursor-pointer truncate border-0 bg-transparent p-0 text-left text-base font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                    data-testid={`documents-row-title-${document.id}`}
                  >
                    {document.title}
                  </button>
                </Tooltip>

                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
                  <Chip
                    label={scopeLabel}
                    size="small"
                    variant="outlined"
                    color={scopeChipColor[document.scope]}
                    className="!max-h-6 !text-xs"
                  />
                  {fileCount > 0 ? (
                    <span className="text-sm text-primary-medium">
                      {t('documents.file-count', {
                        ns: 'components',
                        count: fileCount,
                      })}
                    </span>
                  ) : null}
                  {document.issuer ? (
                    <span className="truncate text-sm text-primary-medium">
                      · {document.issuer}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        },
      }),

      columnHelper.accessor('category', {
        header: t('documents.table.columns.category', { ns: 'components' }),
        minSize: 176,
        size: 192,
        cell: info => (
          <Tooltip
            title={categoryLabels[info.getValue()] ?? info.getValue()}
            placement="top"
          >
            <span className="block truncate text-primary-medium">
              {categoryLabels[info.getValue()] ?? info.getValue()}
            </span>
          </Tooltip>
        ),
      }),

      columnHelper.accessor('subcategory', {
        header: t('documents.table.columns.subcategory', { ns: 'components' }),
        minSize: 176,
        size: 192,
        cell: info => (
          <Tooltip
            title={subcategoryLabels[info.getValue()] ?? info.getValue()}
            placement="top"
          >
            <span className="block truncate text-primary-medium">
              {subcategoryLabels[info.getValue()] ?? info.getValue()}
            </span>
          </Tooltip>
        ),
      }),

      columnHelper.accessor('document_date', {
        header: t('documents.table.columns.document-date', { ns: 'components' }),
        enableHiding: false,
        sortingFn: 'datetime',
        minSize: 136,
        size: 152,
        maxSize: 168,
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="date"
            value={info.getValue()}
            safeValue="—"
            formatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }}
          />
        ),
      }),

      columnHelper.accessor('expiry_date', {
        header: t('documents.table.columns.expiry-date', { ns: 'components' }),
        enableHiding: false,
        sortingFn: 'datetime',
        minSize: 136,
        size: 152,
        maxSize: 168,
        meta: { alignment: 'end' },
        cell: info => {
          const value = info.getValue();
          const expiryHint = getDocumentExpiryHint(
            value,
            i18n.language,
            new Date(),
          );

          if (!value) {
            return <span className="text-primary-medium">—</span>;
          }

          const dateLabel = (
            <Table.Data
              format="date"
              value={value}
              formatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }}
              container={false}
            />
          );

          return (
            <Tooltip title={expiryHint.tooltip} placement="top">
              <span className="inline-flex items-center justify-end gap-x-2">
                {dateLabel}
                {expiryHint.dot !== 'none' && (
                  <span
                    className={`size-2 shrink-0 rounded-full ${
                      expiryDotClassName[expiryHint.dot]
                    }`}
                  />
                )}
              </span>
            </Tooltip>
          );
        },
      }),

      columnHelper.display({
        id: 'options',
        enableSorting: false,
        enableHiding: false,
        minSize: 140,
        size: 140,
        maxSize: 140,
        meta: { alignment: 'end' },
        cell: info => (
          <DocumentsRowActions
            document={info.row.original}
            onDownload={onDownload}
            onViewDetail={onViewDetail}
            onDelete={onDelete}
          />
        ),
      }),
    ];
  }, [
    categoryLabels,
    i18n.language,
    onDownload,
    onDelete,
    onViewDetail,
    subcategoryLabels,
    t,
  ]);

  return {
    columns,
    data,
  };
};
